// ArcGIS field-record push: daily report captures written to a customer's
// ArcGIS feature layers. Shapes mirror the API's /v2 arcgis-push and
// arcgis-connection responses (plain JSON, no serializer envelope).

export type ArcgisLayerKind = 'point' | 'line' | 'polygon';
export type ArcgisPushTrigger = 'submit' | 'approve' | 'save';
export type ArcgisTargetStatus = 'ok' | 'auth_failed' | 'schema_invalid' | null;
export type ArcgisAuthType = 'oauth_app' | 'user';
export type ArcgisLayerSource = 'project' | 'org';

// The three layer URLs as the API names them (project target and org defaults).
export interface ArcgisLayerUrls {
  point_layer_url: string | null;
  line_layer_url: string | null;
  polygon_layer_url: string | null;
}

export interface ArcgisPushTarget {
  id: string;
  enabled: boolean;
  point_layer_url: string | null;
  line_layer_url: string | null;
  polygon_layer_url: string | null;
  layer_source: ArcgisLayerSource;
  trigger: ArcgisPushTrigger;
  status: ArcgisTargetStatus;
  status_message: string | null;
  checked_at: string | null;
  last_pushed_at: string | null;
}

export interface ArcgisPushFailure {
  job_id: string;
  daily_report_id: string;
  site_id: string | null;
  report_date: string; // YYYY-MM-DD
  failure_code: string | null;
  message: string | null;
  attempts: number;
  failed_at: string;
}

export interface ArcgisPushStatus {
  target: ArcgisPushTarget | null;
  // The org's default layers are read-only here.
  connection: { configured: boolean; portal_url: string | null } & ArcgisLayerUrls;
  pushed: { features: number; reports: number };
  pending: number;
  failed: number;
  failures: ArcgisPushFailure[];
}

export interface ArcgisLayerCheck {
  url: string;
  ok: boolean;
  errors: string[];
  warnings: string[];
  transient?: boolean;
}

export interface ArcgisPreflight {
  ok: boolean;
  code?: string;
  message?: string;
  layers: Partial<Record<ArcgisLayerKind, ArcgisLayerCheck>>;
}

export interface ArcgisPushTargetInput extends ArcgisLayerUrls {
  enabled: boolean;
  trigger: ArcgisPushTrigger;
  layer_source: ArcgisLayerSource;
}

export interface ArcgisPushSaveResponse {
  target: ArcgisPushTarget;
  preflight: ArcgisPreflight | null;
}

export interface ArcgisConnection extends ArcgisLayerUrls {
  portal_url: string | null;
  auth_type: ArcgisAuthType | null;
  client_id: string | null;
  username: string | null;
  has_secret: boolean;
  layers_checked_at: string | null;
}

export interface ArcgisConnectionInput {
  portal_url: string;
  auth_type: ArcgisAuthType;
  client_id?: string;
  username?: string;
  secret?: string; // omitted keeps the stored one
  point_layer_url?: string | null; // the three layer fields omitted = leave the org layers as they are
  line_layer_url?: string | null;
  polygon_layer_url?: string | null;
}

export interface ArcgisConnectionTest {
  ok: boolean;
  code?: string;
  message: string;
}

export interface ArcgisConnectionSaveResponse {
  connection: ArcgisConnection;
  test: ArcgisConnectionTest;
  preflight?: ArcgisPreflight | null; // when the org layers were part of the save
  projects?: number; // projects on org layers whose reports were re-checked
}

// Same rule as the API's layer URL validation.
export const ARCGIS_LAYER_URL_PATTERN = /^https?:\/\/[^?#]+\/FeatureServer\/\d+\/?$/i;
